import React, {useContext} from 'react'
import clsx from 'clsx'
import {orderBy} from 'lodash'
import {Link, useLocation} from 'react-router-dom'
import {makeStyles} from 'external/material'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableBody from '@mui/material/TableBody'

import GradeInput from 'components/gradebook/GradeInput'
import {createPost} from 'helpers/command'

import moment from 'helpers/moment'
import {TeacherContext} from 'teacher/context'
import {displayPoints} from 'helpers/gradebook'

const useStyles = makeStyles(studentAnsweredAssessmentsBodyStyles)

const sorters = {
  date: (items, order) => orderBy(items, [a => moment(a.finished).valueOf()], [order]),
  title: (items, order) => orderBy(items, ['title'], [order]),
  period: (items, order) => orderBy(items, [a =>
    (a.period && a.period.properties && a.period.properties.name) || ''], [order]),
  type: (items, order) => orderBy(items, ['typeName'], [order]),
  total: (items, order) => orderBy(items, ['total'], [order]),
  points: (items, order) => orderBy(items, ['points'], [order]),
  grade: (items, order) => orderBy(items, ['grade'], [order])
}

export default function StudentAnsweredAssessmentsBody() {

  const classes = useStyles()
  const context = useContext(TeacherContext)
  const {query, translate, link, gradingCategoryMap, studentId, updateState} = context
  const location = useLocation()
  const order = query.orderBy ? (query.orderBy.startsWith('-') ? 'desc' : 'asc') : undefined
  const orderColumn = order ? (order == 'asc' ? query.orderBy : query.orderBy.substring(1)) : undefined

  const studentAssessmnents = context.rollbookContextGrades.items.filter(g => g.student == studentId)

  const dateFormat = translate('assessment-list-date-format')

  const summary = ({total, points, properties}) => {
    const adjusted = points + (properties?.bonus || 0)
    return total == 0 ? '0%' : `${(adjusted / total * 100).toFixed(2)}%`
  }
  const getPeriod = a => context.rollbookContextPeriods.items.find(period => moment(a.start).isSameOrAfter(period.start) && moment(a.start).isSameOrBefore(period.finish)) || {}

  const translatedItems = studentAssessmnents.map(i => ({
    ...i,
    typeName: translate(`${i.type == 'homework' ? 'homework' : gradingCategoryMap[i.type]}-label`),
    statusName: translate(i.status),
    period: getPeriod(i),
    grade: summary(i)
  }))

  let filteredItems = translatedItems
  if (query.periodId) filteredItems = filteredItems.filter(i => i.period.id == query.periodId)
  if (query.typeId) filteredItems = filteredItems.filter(i => i.type == query.typeId)

  const sorter = sorters[orderColumn]
  const assessments = sorter
    ? sorter(filteredItems, order)
    : filteredItems

  return (<TableBody>{assessments.map((assessment, index) => {
    const {typeName, external, type, period, title, finished, total, points, grade, answeredAssessment} = assessment

    const reviewLink = type == 'homework'
      ? link({page: 'homework', id: assessment.assignment}, '/:languageId/:role/:page/:id')
      : link({page: 'quiz', assessmentId: answeredAssessment, action: 'review'}, '/:languageId/:role/:page/:assessmentId/:action')

    function reviewClickHandler() {
      const commands = type == 'homework'
        ? {homeworkContext: {review: {from: {$set: location.pathname}, query: {$set: query}}}}
        : {quizEditFrom: {$set: location.pathname}, quizEditFromQuery: {$set: query}}
      updateState(commands)
    }

    const periodName = period.properties && period.properties.name


    async function handleGradeChange (value) {

      const [grade] = await createPost('grading', 'updateExternalStudentGrade')({student: studentId, external, value: value || 0})
      if (grade) {
        const index = rollbookContextGrades.items.findIndex(g => g.id === grade.id)
        if (index >= 0) {
          updateState({rollbookContextGrades: {items: {[index]: {$set: grade}}}})
        } else {
          updateState({rollbookContextGrades: {items: {$push: [grade]}}})
          
        }
      }

    }

  

    return (
      <TableRow key={index} className={classes.assessmentRow}>
        <TableCell align="left" className={clsx(classes.cell, classes.dateCell)}>
          {moment(finished).format(dateFormat)}
        </TableCell>
        <TableCell align="left" className={clsx(classes.cell, classes.titleCell)}>
          <span className="label">{title}</span>
        </TableCell>
        <TableCell align="left" className={clsx(classes.cell, classes.periodCell)}>
          <span className="label">{periodName}</span>
        </TableCell>
        <TableCell align="left" className={clsx(classes.cell, classes.typeCell)}>
          {typeName}
        </TableCell>
        <TableCell align="center" className={clsx(classes.cell, classes.totalCell)}>
          {total}
        </TableCell>
      <TableCell align="center" className={clsx(classes.cell, classes.pointsCell)}>
        {external
          ? <GradeInput total={total} value={displayPoints(assessment)} onGradeChange={handleGradeChange} className={classes.gradeInput} />
          : <Link to={reviewLink} onClick={reviewClickHandler} className={classes.link}>{displayPoints(assessment)}</Link>
        }
       </TableCell>
      <TableCell align="center" className={clsx(classes.cell, classes.gradeCell)}>
        {grade}
      </TableCell>
       <TableCell align="center" className={clsx(classes.cell, classes.filler)} />
      </TableRow>
    )
  })}</TableBody>)
}
 
function studentAnsweredAssessmentsBodyStyles() {
  return {
    gradeInput: {
      marginTop: -15
    },
    assessmentRow: {
      transition: 'background-color .5s',
      '&:hover': {
          backgroundColor: '#f8f8f8'
      }
    },
    cell: {
      borderLeft: 'solid 1px #d8dbe0',
      borderRight: 'solid 1px #d8dbe0',
      '&:first-child': {
        borderLeft: 0
      },
      '&.filler': {
        borderLeft: 'solid 1px #d8dbe0',
        width: 0,
        padding: 0,
      },
    },
    link: {
      color: '#009dd8',
      cursor: 'pointer',
      textDecoration: 'none',
      '&:hover': {
        fontWeight: 'bold',
        textDecoration: 'underline'
      }
    },
  }
}
